<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="配置 Boss 圈侧栏「推荐功能」「平台功能」入口。跳转功能从 Boss 圈功能目录自动读取，新增功能后刷新本页即可出现在下拉中。"
    />
    <el-form :inline="true" class="mb-12px" @submit.prevent>
      <el-form-item label="分组">
        <el-select v-model="queryGroup" clearable placeholder="全部" style="width: 160px" @change="load">
          <el-option
            v-for="item in groupOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button v-hasPermi="['bosscircle:home-feature:create']" type="primary" @click="open()">
          <Icon class="mr-5px" icon="ep:plus" />新增功能
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="图标" width="88">
        <template #default="{ row }">
          <el-image
            v-if="row.iconUrl"
            :src="row.iconUrl"
            fit="contain"
            style="width: 48px; height: 48px"
            :preview-src-list="[row.iconUrl]"
            preview-teleported
          />
          <span v-else class="text-gray-400">默认</span>
        </template>
      </el-table-column>
      <el-table-column label="分组" width="110">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.BOSSCIRCLE_HOME_FEATURE_GROUP" :value="row.groupCode" />
        </template>
      </el-table-column>
      <el-table-column prop="name" label="名称" min-width="100" show-overflow-tooltip />
      <el-table-column prop="featureCode" label="编码" min-width="120" show-overflow-tooltip />
      <el-table-column prop="path" label="跳转路径" min-width="220" show-overflow-tooltip />
      <el-table-column label="需登录" width="90">
        <template #default="{ row }">
          <el-tag :type="row.needLogin ? 'warning' : 'info'" effect="plain">
            {{ row.needLogin ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="80" />
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="row.status" />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="210" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['bosscircle:home-feature:update']"
            link
            type="primary"
            @click="open(row)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['bosscircle:home-feature:update']"
            link
            :type="row.status === 0 ? 'warning' : 'success'"
            @click="toggleStatus(row)"
          >
            {{ statusActionLabel(row.status) }}
          </el-button>
          <el-button
            v-hasPermi="['bosscircle:home-feature:delete']"
            link
            type="danger"
            @click="remove(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog v-model="visible" :title="form.id ? '编辑功能' : '新增功能'" width="680px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-form-item label="分组" prop="groupCode">
        <el-select v-model="form.groupCode" class="w-full" placeholder="请选择分组">
          <el-option
            v-for="item in groupOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="名称" prop="name">
        <el-input v-model="form.name" maxlength="64" placeholder="侧栏展示名称" />
      </el-form-item>
      <el-form-item label="功能编码">
        <el-input
          v-model="form.featureCode"
          maxlength="64"
          placeholder="选填；如 recommend_people，便于 C 端兜底图标"
        />
      </el-form-item>
      <el-form-item label="跳转功能" prop="featureCode">
        <el-select
          v-model="form.featureCode"
          class="w-full"
          filterable
          placeholder="请选择 Boss 圈功能"
          @change="onFeaturePick"
        >
          <el-option
            v-for="item in catalogOptions"
            :key="item.code"
            :label="item.label || item.name"
            :value="item.code"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="图标">
        <UploadImg v-model="form.iconUrl" />
        <div class="el-upload__tip" style="line-height: 1.4; margin-top: 6px">
          建议正方形 PNG；不传则 C 端按编码/名称使用本地默认图标
        </div>
      </el-form-item>
      <el-form-item label="需登录" prop="needLogin">
        <el-switch v-model="form.needLogin" />
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="form.sort" :min="0" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio
            v-for="item in statusOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import * as Api from '@/api/business/homeFeature'
import { DICT_TYPE, getIntDictOptions, getStrDictOptions } from '@/utils/dict'

defineOptions({ name: 'BossCircleHomeFeature' })

const message = useMessage()
const catalogOptions = ref<Api.BossCircleFeatureOption[]>([])
const loading = ref(false)
const saving = ref(false)
const visible = ref(false)
const queryGroup = ref<string>()
const list = ref<Api.BossCircleHomeFeatureVO[]>([])
const formRef = ref<FormInstance>()
const groupOptions = getStrDictOptions(DICT_TYPE.BOSSCIRCLE_HOME_FEATURE_GROUP)
const statusOptions = getIntDictOptions(DICT_TYPE.COMMON_STATUS)

const defaults = (): Api.BossCircleHomeFeatureVO => ({
  groupCode: 'RECOMMEND',
  featureCode: '',
  name: '',
  iconUrl: '',
  path: '',
  needLogin: false,
  sort: 0,
  status: 0
})
const form = reactive<Api.BossCircleHomeFeatureVO>(defaults())
const rules: FormRules = {
  groupCode: [{ required: true, message: '请选择分组', trigger: 'change' }],
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
  featureCode: [{ required: true, message: '请选择跳转功能', trigger: 'change' }],
  needLogin: [{ required: true, message: '请选择是否需登录', trigger: 'change' }],
  sort: [{ required: true, message: '请输入排序', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

const statusActionLabel = (current?: number) => {
  const next = current === 0 ? 1 : 0
  return statusOptions.find((item) => item.value === next)?.label || '切换'
}

const load = async () => {
  loading.value = true
  try {
    list.value = await Api.getHomeFeatureList(queryGroup.value || undefined)
  } finally {
    loading.value = false
  }
}

const onFeaturePick = (code: string) => {
  const picked = catalogOptions.value.find((item) => item.code === code)
  if (!picked) return
  form.featureCode = picked.code
  form.path = picked.path
  form.needLogin = !!picked.needLogin
  if (!form.name) form.name = picked.name
}

const open = (row?: Api.BossCircleHomeFeatureVO) => {
  Object.assign(form, defaults(), row || {})
  if (row?.path && !catalogOptions.value.some((item) => item.code === row.featureCode)) {
    const matched = catalogOptions.value.find((item) => item.path === row.path)
    if (matched) form.featureCode = matched.code
  }
  visible.value = true
  nextTick(() => formRef.value?.clearValidate())
}

const save = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    form.id ? await Api.updateHomeFeature(form) : await Api.createHomeFeature(form)
    message.success('保存成功')
    visible.value = false
    await load()
  } finally {
    saving.value = false
  }
}

const toggleStatus = async (row: Api.BossCircleHomeFeatureVO) => {
  const nextStatus = row.status === 0 ? 1 : 0
  const label = statusOptions.find((item) => item.value === nextStatus)?.label || '操作'
  await message.confirm(`确认将该功能设为「${label}」吗？`)
  await Api.updateHomeFeature({ ...row, status: nextStatus })
  message.success('操作成功')
  await load()
}

const remove = async (id?: number) => {
  if (!id) return
  await message.confirm('确认删除该功能吗？')
  await Api.deleteHomeFeature(id)
  message.success('删除成功')
  await load()
}

onMounted(async () => {
  try {
    catalogOptions.value = (await Api.getHomeFeatureCatalog()) || []
  } catch {
    catalogOptions.value = []
  }
  await load()
})
</script>
